import { createContext, useContext, useEffect, useMemo, useState } from "react";

const en = {
  nav: {
    links: ["How it Works", "Platform", "Pricing", "Stories"],
    login: "Log In",
    start: "Start Closing",
  },
  hero: {
    badge: "Now in Ontario & Alberta",
    lines: ["Every deal", "deserves a", "clean close."],
    sub: "Settld pairs artificial intelligence with licensed real estate counsel to close your property in days, not months — fully digital, flat-fee, zero surprises.",
    quoteCta: "Get a Quote — Free",
    demoCta: "See how it works",
    stats: [
      { value: "2,400+", label: "Closings completed" },
      { value: "11 days", label: "Average close time" },
      { value: "4.9/5", label: "Client rating" },
    ],
    chip: "Settld AI reviewing agreement",
  },
  dash: {
    live: "Live Closing",
    property: "742 Queen St W, Toronto",
    price: "Purchase · $1,150,000 CAD",
    progress: "Closing Progress",
    steps: [
      { label: "File Opened", date: "Jul 14" },
      { label: "Title Search", date: "Jul 16" },
      { label: "AI Document Review", date: "In progress" },
      { label: "Lawyer Sign-Off", date: "Jul 24" },
      { label: "Digital Close", date: "Jul 28" },
    ],
    aiTitle: "Settld AI",
    aiMsg: "Reviewing purchase agreement — 3 clauses flagged",
  },
  marquee: [
    "AI-Powered Closing",
    "Licensed Real Estate Counsel",
    "Flat-Fee Pricing",
    "100% Digital Signing",
    "Law Society Authorized",
    "2,400+ Closings Completed",
  ],
  problem: {
    label: "The Problem",
    titleA: "Canadian real estate still closes like it’s",
    titleB: "1998.",
    intro:
      "The conveyancing industry moves billions of dollars a year through a process that relies on paper documents, in-person signings, and phone tag. Settld exists because that’s indefensible in 2026.",
    cards: [
      {
        title: "Paper overload",
        desc: "The average closing generates 100+ pages of documents. Most are reviewed exactly once — by a tired paralegal at 11pm.",
      },
      {
        title: "Weeks of waiting",
        desc: "6–8 weeks is the Canadian industry norm. Delays cascade from title search bottlenecks, manual scheduling, and fax-era workflows.",
      },
      {
        title: "Fee ambiguity",
        desc: "Hidden disbursements, surprise courier charges, rush fees buried in fine print. Most buyers don't know the real cost until the invoice arrives.",
      },
      {
        title: "Office visits",
        desc: "Signing appointments that require you to take time off work, find parking, and sit in a waiting room — for documents you've already read online.",
      },
    ],
  },
  hiw: {
    label: "How Settld Works",
    titleA: "Four phases.",
    titleB: "One seamless close.",
    sub: "From first upload to final signature, every stage of your closing moves through one continuous digital pipeline — watched over by AI and certified by a licensed lawyer.",
    cta: "Start your file",
    phases: [
      {
        tag: "Intake",
        title: "Open your file in 5 minutes",
        desc: "Tell Settld about your transaction — purchase, sale, refinance, or transfer. We create a secure digital workspace and generate a tailored checklist. No PDFs, no printing, no office visit.",
      },
      {
        tag: "Intelligence",
        title: "AI reads every line",
        desc: "Our models parse your purchase agreement, title history, mortgage terms, and disclosure documents. We surface clause-level risks, missing conditions, and compliance gaps — in seconds, not days.",
      },
      {
        tag: "Certification",
        title: "Your lawyer takes the wheel",
        desc: "A licensed real estate lawyer reviews the AI findings, consults with you over video, and certifies the closing. Machine speed, human judgment, full professional accountability.",
      },
      {
        tag: "Settlement",
        title: "Sign, fund, close",
        desc: "Execute all documents with legally binding eSignatures from any device. Funds transfer through encrypted channels. You receive your keys and a complete digital closing package.",
      },
    ],
  },
  platform: {
    label: "The Platform",
    titleA: "Built for the transaction,",
    titleB: "not the paperwork.",
    features: [
      {
        title: "Clause-Level AI Review",
        desc: "Settld's models don't skim — they parse every clause, condition, and schedule in your documents, cross-referencing against provincial statutes and flagging risk at the sentence level.",
      },
      {
        title: "Live Deal Dashboard",
        desc: "A real-time view of your closing status, document checklist, upcoming milestones, and lawyer availability. No more email chains asking 'where are we at?'.",
      },
      {
        title: "Licensed Counsel at Every Close",
        desc: "Every Settld transaction is reviewed and certified by a licensed real estate lawyer in your province. AI accelerates the work — it never replaces the legal judgment.",
      },
      {
        title: "End-to-End Encryption",
        desc: "AES-256 document encryption, SOC 2 Type II infrastructure, and secure wire protocols for fund transfers. Your closing data is protected at rest and in transit.",
      },
      {
        title: "Remote eSign & Video Consult",
        desc: "Legally binding digital signatures from any device. Lawyer consultations by video call. Close from your kitchen table, your office, or a beach in Portugal.",
      },
      {
        title: "24/7 AI Concierge + Human Escalation",
        desc: "Instant answers from Settld's AI assistant any time of day. When you need a human, a licensed professional responds within two business hours.",
      },
    ],
  },
  comparison: {
    label: "Why Settld",
    titleA: "A side-by-side that",
    titleB: "speaks for itself.",
    tradHeader: "Traditional",
    rows: [
      { label: "Average time to close", settld: "11 days", trad: "6–8 weeks" },
      { label: "Process", settld: "100% digital", trad: "Paper + office visits" },
      { label: "Pricing model", settld: "Flat fee, known upfront", trad: "Hourly + hidden extras" },
      { label: "Document review", settld: "AI + lawyer", trad: "Manual only" },
      { label: "Status visibility", settld: "Real-time dashboard", trad: "Call & ask" },
      { label: "Availability", settld: "24/7 AI + business hours", trad: "Business hours only" },
      { label: "Document security", settld: "AES-256 encrypted", trad: "Filing cabinet" },
      { label: "Signing method", settld: "Legal eSignature", trad: "Wet ink in-office" },
    ],
  },
  pricing: {
    label: "Pricing",
    titleA: "Flat fees. Full transparency.",
    titleB: "Always.",
    sub: "You’ll know the exact legal cost before you commit. Disbursements are passed through at cost — zero markup.",
    tabs: [
      { id: "purchase", label: "Purchase & Sale" },
      { id: "refinance", label: "Refinance" },
      { id: "transfer", label: "Title Transfer" },
    ],
    popular: "Most Popular",
    getStarted: "Get Started",
    contactUs: "Contact Us",
    plans: {
      purchase: [
        {
          name: "Standard", subtitle: "Single purchase or sale", price: "$1,190",
          note: "+ applicable disbursements · HST excluded", popular: false,
          features: ["Full title search & review", "AI-powered document analysis", "Licensed lawyer certification", "Legal eSignature", "Live closing dashboard", "Secure fund handling"],
        },
        {
          name: "Paired", subtitle: "Purchase + Sale together", price: "$1,999",
          note: "Save $380 vs. two separate closings", popular: true,
          features: ["Everything in Standard", "Simultaneous close coordination", "Bridge financing support", "Priority lawyer access", "Same-day response SLA", "Dedicated closing coordinator"],
        },
        {
          name: "Volume", subtitle: "Agents & investors", price: "Custom",
          note: "Starting from $899 / closing", popular: false,
          features: ["Everything in Paired", "Volume discounts (5+ closings)", "Branded client portal", "Agent reporting dashboard", "API & MLS integration", "Priority SLA support"],
        },
      ],
      refinance: [
        {
          name: "Standard", subtitle: "Mortgage refinance", price: "$990",
          note: "+ applicable disbursements · HST excluded", popular: false,
          features: ["Mortgage discharge review", "New mortgage registration", "Title search & confirmation", "AI document analysis", "Licensed lawyer certification", "Digital signing"],
        },
        {
          name: "Priority", subtitle: "Fast-track refinance", price: "$1,090",
          note: "Same-day lawyer response included", popular: true,
          features: ["Everything in Standard", "Same-day lawyer response", "Lender direct communication", "Rush closing available", "Video lawyer consultation", "Priority fund disbursement"],
        },
        {
          name: "Portfolio", subtitle: "Multiple properties", price: "Custom",
          note: "Starting from $690 / property", popular: false,
          features: ["Everything in Priority", "Multi-property coordination", "Volume discounts (3+)", "Unified billing & reporting", "Dedicated account manager", "Custom SLA agreements"],
        },
      ],
      transfer: [
        {
          name: "Simple", subtitle: "Title-only transfer", price: "$790",
          note: "+ applicable disbursements · HST excluded", popular: false,
          features: ["Title deed preparation", "AI document review", "Title search & confirmation", "Licensed lawyer certification", "Digital signing", "Land registry update"],
        },
        {
          name: "With Mortgage", subtitle: "Transfer + mortgage change", price: "$1,090",
          note: "Full mortgage discharge & registration", popular: true,
          features: ["Everything in Simple", "Mortgage discharge & registration", "Lender coordination", "Priority lawyer access", "Same-day response SLA", "Video consultation"],
        },
        {
          name: "Estate", subtitle: "Estate & probate transfers", price: "Custom",
          note: "Based on estate complexity", popular: false,
          features: ["Everything in With Mortgage", "Estate document review", "Beneficiary coordination", "CRA clearance guidance", "Dedicated estate lawyer", "Extended support hours"],
        },
      ],
    },
  },
  testimonials: {
    label: "Client Stories",
    titleA: "What our clients",
    titleB: "actually say.",
    items: [
      {
        text: "We closed on our first home in nine days. Nine. Our agent couldn't believe it — she said her previous clients waited two months with a traditional firm. The dashboard alone is worth switching for.",
        name: "Priya & Arjun M.", role: "First-time Buyers · Mississauga, ON", initials: "PM",
      },
      {
        text: "I run a boutique brokerage and I've moved all my closings to Settld. My clients love the transparency, I love the agent dashboard, and the AI catches things my old law firm missed. It's a competitive advantage.",
        name: "David K.", role: "Real Estate Agent · Calgary, AB", initials: "DK",
      },
      {
        text: "Sold our family home and bought a condo on the same day — Settld coordinated both closings seamlessly. Everything was handled over Zoom and eSign. We never stepped foot in an office.",
        name: "Catherine L.", role: "Home Seller & Buyer · Toronto, ON", initials: "CL",
      },
    ],
  },
  trust: {
    label: "Trust & Compliance",
    titleA: "Built on",
    titleB: "verified trust.",
    items: [
      {
        title: "Law Society Authorized",
        desc: "Settld operates as a licensed legal services provider, authorized by the Law Societies of Ontario and Alberta to deliver technology-enabled conveyancing.",
      },
      {
        title: "SOC 2 Type II Compliant",
        desc: "Enterprise-grade infrastructure with continuous monitoring, AES-256 encryption, and annual third-party audits. Your documents and funds are protected at every layer.",
      },
      {
        title: "Errors & Omissions Insured",
        desc: "Full professional liability coverage as required by provincial law. Every closing also includes title insurance from a major Canadian underwriter.",
      },
      {
        title: "AI + Human, Always",
        desc: "Our AI accelerates review, surfaces risk, and eliminates manual errors. A licensed lawyer certifies every closing. You never have to choose between speed and judgment.",
      },
    ],
    bandKicker: "Licensed & Local",
    bandTitle: "Authorized to close in Ontario & Alberta.",
    bandAlt: "Toronto skyline at sunset",
  },
  quote: {
    label: "Get Started",
    titleA: "Your next closing",
    titleB: "starts here.",
    sub: "Get a free quote in under two minutes. No commitment, no credit card, no fine print.",
    promises: ["Free quote in under two minutes", "No commitment, no credit card", "Licensed lawyer review included"],
    formTitle: "Get a free quote",
    formSub: "We respond within one business day.",
    fields: {
      name: "Full Name",
      email: "Email",
      phone: "Phone (optional)",
      type: "Transaction Type",
      province: "Province",
      message: "Anything we should know? (optional)",
    },
    placeholders: {
      name: "Jane Doe",
      email: "jane@email.com",
      phone: "(416) 555-0100",
      message: "Closing date, property type, questions…",
    },
    types: ["Purchase", "Sale", "Purchase + Sale", "Refinance", "Title Transfer"],
    provinces: ["Ontario", "Alberta", "British Columbia", "Other"],
    submit: "Get My Free Quote",
    sending: "Sending…",
    success: "Quote request received — a closing specialist will reach out within one business day.",
    error: "Something went wrong. Please try again.",
  },
  concierge: {
    title: "Settld AI Concierge",
    subtitle: "Online · replies instantly",
    greeting: "Hi! I'm the Settld AI concierge. Ask me anything about digital closings, pricing, or how Settld works.",
    placeholder: "Ask about your closing…",
    send: "Send message",
    errorMsg: "Sorry, I couldn't respond right now. Please try again.",
  },
  footer: {
    tagline: "AI-Powered Digital Closing · Licensed in Ontario & Alberta",
    columns: [
      { title: "Product", links: ["How It Works", "Platform", "Pricing", "For Agents"] },
      { title: "Company", links: ["About", "Careers", "Blog", "Contact"] },
      { title: "Legal", links: ["Privacy Policy", "Terms of Service", "Cookie Policy"] },
    ],
    rights: "© 2026 Settld Technologies Inc. All rights reserved.",
    authorized: "Authorized by the Law Societies of Ontario & Alberta",
  },
};

const fr = {
  nav: {
    links: ["Comment ça marche", "Plateforme", "Tarifs", "Témoignages"],
    login: "Connexion",
    start: "Conclure",
  },
  hero: {
    badge: "Maintenant en Ontario et en Alberta",
    lines: ["Chaque transaction", "mérite une", "clôture impeccable."],
    sub: "Settld allie l'intelligence artificielle à l'expertise d'avocats en droit immobilier pour conclure votre transaction en quelques jours, pas en mois — entièrement numérique, à forfait, sans surprises.",
    quoteCta: "Soumission gratuite",
    demoCta: "Voir comment ça marche",
    stats: [
      { value: "2 400+", label: "Clôtures complétées" },
      { value: "11 jours", label: "Délai moyen de clôture" },
      { value: "4,9/5", label: "Note des clients" },
    ],
    chip: "L'IA Settld analyse l'entente",
  },
  dash: {
    live: "Clôture en direct",
    property: "742 Queen St W, Toronto",
    price: "Achat · 1 150 000 $ CA",
    progress: "Progression de la clôture",
    steps: [
      { label: "Dossier ouvert", date: "14 juill." },
      { label: "Recherche de titres", date: "16 juill." },
      { label: "Révision IA des documents", date: "En cours" },
      { label: "Approbation de l'avocat", date: "24 juill." },
      { label: "Clôture numérique", date: "28 juill." },
    ],
    aiTitle: "IA Settld",
    aiMsg: "Analyse de la promesse d'achat — 3 clauses signalées",
  },
  marquee: [
    "Clôture propulsée par l'IA",
    "Avocats immobiliers agréés",
    "Tarification forfaitaire",
    "Signature 100 % numérique",
    "Autorisé par les barreaux",
    "2 400+ clôtures complétées",
  ],
  problem: {
    label: "Le problème",
    titleA: "L'immobilier canadien se clôture encore comme en",
    titleB: "1998.",
    intro:
      "L'industrie du transfert de propriété fait circuler des milliards de dollars par année au moyen de documents papier, de signatures en personne et de tag téléphonique. Settld existe parce que c'est indéfendable en 2026.",
    cards: [
      {
        title: "Surcharge de papier",
        desc: "Une clôture moyenne génère plus de 100 pages de documents. La plupart ne sont relues qu'une seule fois — par un parajuriste fatigué à 23 h.",
      },
      {
        title: "Des semaines d'attente",
        desc: "Six à huit semaines : c'est la norme canadienne. Les retards s'enchaînent — goulots des recherches de titres, planification manuelle et processus d'un autre siècle.",
      },
      {
        title: "Frais opaques",
        desc: "Débours cachés, frais de messagerie surprises, frais d'urgence enfouis dans les petits caractères. La plupart des acheteurs découvrent le coût réel à l'arrivée de la facture.",
      },
      {
        title: "Visites au bureau",
        desc: "Des séances de signature qui vous obligent à prendre congé, à trouver un stationnement et à patienter en salle d'attente — pour des documents déjà lus en ligne.",
      },
    ],
  },
  hiw: {
    label: "Comment Settld fonctionne",
    titleA: "Quatre phases.",
    titleB: "Une clôture sans faille.",
    sub: "Du premier téléversement à la dernière signature, chaque étape de votre clôture circule dans un pipeline numérique continu — surveillé par l'IA et certifié par un avocat agréé.",
    cta: "Ouvrir mon dossier",
    phases: [
      {
        tag: "Ouverture",
        title: "Ouvrez votre dossier en 5 minutes",
        desc: "Décrivez votre transaction à Settld — achat, vente, refinancement ou transfert. Nous créons un espace de travail numérique sécurisé et générons une liste de vérification sur mesure. Aucun PDF, aucune impression, aucune visite au bureau.",
      },
      {
        tag: "Intelligence",
        title: "L'IA lit chaque ligne",
        desc: "Nos modèles analysent votre promesse d'achat, l'historique des titres, les conditions hypothécaires et les documents de divulgation. Nous relevons les risques clause par clause, les conditions manquantes et les lacunes de conformité — en secondes, pas en jours.",
      },
      {
        tag: "Certification",
        title: "Votre avocat prend le relais",
        desc: "Un avocat en droit immobilier agréé examine les constats de l'IA, vous consulte par vidéo et certifie la clôture. La vitesse de la machine, le jugement d'un humain, une responsabilité professionnelle entière.",
      },
      {
        tag: "Règlement",
        title: "Signez, transférez, concluez",
        desc: "Signez tous les documents par signature électronique juridiquement contraignante, depuis n'importe quel appareil. Les fonds sont transférés par des canaux chiffrés. Vous recevez vos clés et un dossier de clôture numérique complet.",
      },
    ],
  },
  platform: {
    label: "La plateforme",
    titleA: "Conçue pour la transaction,",
    titleB: "pas la paperasse.",
    features: [
      {
        title: "Révision IA clause par clause",
        desc: "Les modèles de Settld ne survolent pas : ils analysent chaque clause, condition et annexe de vos documents, en recoupant les lois provinciales, et signalent les risques à l'échelle de la phrase.",
      },
      {
        title: "Tableau de bord en direct",
        desc: "Une vue en temps réel de l'état de votre clôture, de la liste des documents, des jalons à venir et des disponibilités de votre avocat. Fini les courriels « où en sommes-nous? ».",
      },
      {
        title: "Avocat agréé à chaque clôture",
        desc: "Chaque transaction Settld est examinée et certifiée par un avocat en droit immobilier agréé de votre province. L'IA accélère le travail — elle ne remplace jamais le jugement juridique.",
      },
      {
        title: "Chiffrement de bout en bout",
        desc: "Chiffrement des documents AES-256, infrastructure SOC 2 Type II et protocoles sécurisés pour les transferts de fonds. Vos données de clôture sont protégées au repos comme en transit.",
      },
      {
        title: "Signature électronique et vidéo",
        desc: "Des signatures numériques juridiquement contraignantes depuis n'importe quel appareil. Des consultations avec un avocat par vidéo. Concluez depuis votre cuisine, votre bureau ou une plage au Portugal.",
      },
      {
        title: "Concierge IA 24/7 + relais humain",
        desc: "Des réponses instantanées de l'assistant IA de Settld, jour et nuit. Quand vous avez besoin d'un humain, un professionnel agréé répond en moins de deux heures ouvrables.",
      },
    ],
  },
  comparison: {
    label: "Pourquoi Settld",
    titleA: "Une comparaison qui",
    titleB: "dit tout.",
    tradHeader: "Traditionnel",
    rows: [
      { label: "Délai moyen de clôture", settld: "11 jours", trad: "6–8 semaines" },
      { label: "Processus", settld: "100 % numérique", trad: "Papier + visites au bureau" },
      { label: "Modèle de tarification", settld: "Forfait connu d'avance", trad: "Taux horaire + extras cachés" },
      { label: "Révision des documents", settld: "IA + avocat", trad: "Manuelle seulement" },
      { label: "Visibilité du statut", settld: "Temps réel", trad: "Il faut appeler" },
      { label: "Disponibilité", settld: "IA 24/7 + heures ouvrables", trad: "Heures ouvrables seulement" },
      { label: "Sécurité des documents", settld: "Chiffrement AES-256", trad: "Classeur" },
      { label: "Méthode de signature", settld: "Signature électronique légale", trad: "Encre, au bureau" },
    ],
  },
  pricing: {
    label: "Tarifs",
    titleA: "Des forfaits. Une transparence",
    titleB: "totale.",
    sub: "Vous connaîtrez le coût juridique exact avant de vous engager. Les débours sont facturés au prix coûtant — aucune majoration.",
    tabs: [
      { id: "purchase", label: "Achat et vente" },
      { id: "refinance", label: "Refinancement" },
      { id: "transfer", label: "Transfert de titre" },
    ],
    popular: "Le plus populaire",
    getStarted: "Commencer",
    contactUs: "Nous joindre",
    plans: {
      purchase: [
        {
          name: "Standard", subtitle: "Achat ou vente unique", price: "1 190 $",
          note: "+ débours applicables · TVH exclue", popular: false,
          features: ["Recherche et examen complets du titre", "Analyse documentaire par IA", "Certification par un avocat agréé", "Signature électronique légale", "Tableau de bord en direct", "Gestion sécurisée des fonds"],
        },
        {
          name: "Jumelé", subtitle: "Achat + vente combinés", price: "1 999 $",
          note: "Économisez 380 $ par rapport à deux clôtures", popular: true,
          features: ["Tout ce qui est inclus dans Standard", "Coordination de clôtures simultanées", "Soutien au financement relais", "Accès prioritaire à l'avocat", "Réponse le jour même (SLA)", "Coordonnateur de clôture attitré"],
        },
        {
          name: "Volume", subtitle: "Courtiers et investisseurs", price: "Sur mesure",
          note: "À partir de 899 $ / clôture", popular: false,
          features: ["Tout ce qui est inclus dans Jumelé", "Rabais volume (5+ clôtures)", "Portail client à votre image", "Tableau de bord pour courtiers", "Intégration API et MLS", "Soutien SLA prioritaire"],
        },
      ],
      refinance: [
        {
          name: "Standard", subtitle: "Refinancement hypothécaire", price: "990 $",
          note: "+ débours applicables · TVH exclue", popular: false,
          features: ["Examen de la mainlevée hypothécaire", "Enregistrement de la nouvelle hypothèque", "Recherche et confirmation du titre", "Analyse documentaire par IA", "Certification par un avocat agréé", "Signature numérique"],
        },
        {
          name: "Prioritaire", subtitle: "Refinancement accéléré", price: "1 090 $",
          note: "Réponse d'un avocat le jour même incluse", popular: true,
          features: ["Tout ce qui est inclus dans Standard", "Réponse d'un avocat le jour même", "Communication directe avec le prêteur", "Clôture express disponible", "Consultation vidéo avec l'avocat", "Débours des fonds prioritaires"],
        },
        {
          name: "Portefeuille", subtitle: "Propriétés multiples", price: "Sur mesure",
          note: "À partir de 690 $ / propriété", popular: false,
          features: ["Tout ce qui est inclus dans Prioritaire", "Coordination multipropriété", "Rabais volume (3+)", "Facturation et rapports unifiés", "Gestionnaire de compte attitré", "Ententes SLA sur mesure"],
        },
      ],
      transfer: [
        {
          name: "Simple", subtitle: "Transfert de titre seulement", price: "790 $",
          note: "+ débours applicables · TVH exclue", popular: false,
          features: ["Préparation de l'acte de transfert", "Révision documentaire par IA", "Recherche et confirmation du titre", "Certification par un avocat agréé", "Signature numérique", "Mise à jour du registre foncier"],
        },
        {
          name: "Avec hypothèque", subtitle: "Transfert + changement d'hypothèque", price: "1 090 $",
          note: "Mainlevée et enregistrement complets", popular: true,
          features: ["Tout ce qui est inclus dans Simple", "Mainlevée et enregistrement hypothécaires", "Coordination avec le prêteur", "Accès prioritaire à l'avocat", "Réponse le jour même (SLA)", "Consultation vidéo"],
        },
        {
          name: "Succession", subtitle: "Transferts successoraux et d'homologation", price: "Sur mesure",
          note: "Selon la complexité de la succession", popular: false,
          features: ["Tout ce qui est inclus dans Avec hypothèque", "Révision des documents successoraux", "Coordination des bénéficiaires", "Conseils sur la décharge de l'ARC", "Avocat successoral attitré", "Heures de soutien prolongées"],
        },
      ],
    },
  },
  testimonials: {
    label: "Témoignages",
    titleA: "Ce que nos clients",
    titleB: "disent vraiment.",
    items: [
      {
        text: "Nous avons conclu l'achat de notre première maison en neuf jours. Neuf. Notre courtière n'en revenait pas — ses clients précédents avaient attendu deux mois avec un cabinet traditionnel. Le tableau de bord, à lui seul, vaut le changement.",
        name: "Priya & Arjun M.", role: "Premiers acheteurs · Mississauga (Ont.)", initials: "PM",
      },
      {
        text: "Je dirige un courtage boutique et j'ai transféré toutes mes clôtures chez Settld. Mes clients adorent la transparence, j'adore le tableau de bord pour courtiers, et l'IA repère des choses que mon ancien cabinet manquait. C'est un avantage concurrentiel.",
        name: "David K.", role: "Courtier immobilier · Calgary (Alb.)", initials: "DK",
      },
      {
        text: "Nous avons vendu notre maison familiale et acheté un condo le même jour — Settld a coordonné les deux clôtures sans accroc. Tout s'est fait par Zoom et par signature électronique. Nous n'avons jamais mis les pieds dans un bureau.",
        name: "Catherine L.", role: "Vendeuse et acheteuse · Toronto (Ont.)", initials: "CL",
      },
    ],
  },
  trust: {
    label: "Confiance et conformité",
    titleA: "Bâti sur une confiance",
    titleB: "vérifiée.",
    items: [
      {
        title: "Autorisé par les barreaux",
        desc: "Settld est un fournisseur de services juridiques agréé, autorisé par les barreaux de l'Ontario et de l'Alberta à offrir des services de transfert de propriété axés sur la technologie.",
      },
      {
        title: "Conforme SOC 2 Type II",
        desc: "Infrastructure de calibre entreprise avec surveillance continue, chiffrement AES-256 et audits annuels par des tiers. Vos documents et vos fonds sont protégés à tous les niveaux.",
      },
      {
        title: "Assurance erreurs et omissions",
        desc: "Couverture complète de responsabilité professionnelle, conformément au droit provincial. Chaque clôture comprend aussi une assurance titres d'un important assureur canadien.",
      },
      {
        title: "IA + humain, toujours",
        desc: "Notre IA accélère la révision, signale les risques et élimine les erreurs manuelles. Un avocat agréé certifie chaque clôture. Vous n'avez jamais à choisir entre la vitesse et le jugement.",
      },
    ],
    bandKicker: "Agréé et local",
    bandTitle: "Autorisé à conclure en Ontario et en Alberta.",
    bandAlt: "La silhouette de Toronto au coucher du soleil",
  },
  quote: {
    label: "Pour commencer",
    titleA: "Votre prochaine clôture",
    titleB: "commence ici.",
    sub: "Obtenez une soumission gratuite en moins de deux minutes. Sans engagement, sans carte de crédit, sans petits caractères.",
    promises: ["Soumission gratuite en moins de deux minutes", "Aucun engagement, aucune carte de crédit", "Révision par un avocat agréé incluse"],
    formTitle: "Obtenez une soumission gratuite",
    formSub: "Nous répondons en un jour ouvrable.",
    fields: {
      name: "Nom complet",
      email: "Courriel",
      phone: "Téléphone (facultatif)",
      type: "Type de transaction",
      province: "Province",
      message: "Quelque chose à nous préciser? (facultatif)",
    },
    placeholders: {
      name: "Jeanne Tremblay",
      email: "jeanne@courriel.ca",
      phone: "(416) 555-0100",
      message: "Date de clôture, type de propriété, questions…",
    },
    types: ["Achat", "Vente", "Achat + vente", "Refinancement", "Transfert de titre"],
    provinces: ["Ontario", "Alberta", "Colombie-Britannique", "Autre"],
    submit: "Obtenir ma soumission",
    sending: "Envoi en cours…",
    success: "Demande de soumission reçue — un spécialiste des clôtures vous contactera d'ici un jour ouvrable.",
    error: "Une erreur est survenue. Veuillez réessayer.",
  },
  concierge: {
    title: "Concierge IA Settld",
    subtitle: "En ligne · répond instantanément",
    greeting: "Bonjour! Je suis le concierge IA de Settld. Posez-moi vos questions sur les clôtures numériques, les tarifs ou le fonctionnement de Settld.",
    placeholder: "Posez votre question…",
    send: "Envoyer",
    errorMsg: "Désolé, je ne peux pas répondre pour le moment. Veuillez réessayer.",
  },
  footer: {
    tagline: "Clôture numérique propulsée par l'IA · Agréé en Ontario et en Alberta",
    columns: [
      { title: "Produit", links: ["Comment ça marche", "Plateforme", "Tarifs", "Pour les courtiers"] },
      { title: "Entreprise", links: ["À propos", "Carrières", "Blogue", "Contact"] },
      { title: "Légal", links: ["Politique de confidentialité", "Conditions d'utilisation", "Politique sur les témoins"] },
    ],
    rights: "© 2026 Technologies Settld inc. Tous droits réservés.",
    authorized: "Autorisé par les barreaux de l'Ontario et de l'Alberta",
  },
};

const translations = { en, fr };

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const IMAGES_DEFAULT = {
  heroTexture:
    "https://images.unsplash.com/photo-1629197520635-16570fbd0bb3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwxfHxhYnN0cmFjdCUyMGx1eHVyeSUyMGdyZWVuJTIwdGV4dHVyZXxlbnwwfHx8fDE3ODY0NjkxMTh8MA&ixlib=rb-4.1.0&q=85",
  skyline:
    "https://images.unsplash.com/photo-1586576782138-19304c43d0e1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA2MjJ8MHwxfHNlYXJjaHwzfHx0b3JvbnRvJTIwc2t5bGluZSUyMHN1bnNldHxlbnwwfHx8fDE3ODY0NjkxMTh8MA&ixlib=rb-4.1.0&q=85",
  logoFull: "/assets/logo.png",
  logoMark: "/assets/logo-mark-badge.png",
};

export const mergeDeep = (base, override) => {
  if (Array.isArray(base) || Array.isArray(override)) {
    return override !== undefined ? override : base;
  }
  if (typeof base === "object" && base !== null) {
    const out = { ...base };
    if (override && typeof override === "object" && !Array.isArray(override)) {
      for (const k of Object.keys(override)) out[k] = mergeDeep(base[k], override[k]);
    }
    return out;
  }
  return override !== undefined ? override : base;
};

export const SECTIONS_DEFAULT = {
  marquee: true,
  problem: true,
  howItWorks: true,
  platform: true,
  comparison: true,
  pricing: true,
  testimonials: true,
  trust: true,
  brandBand: true,
  quote: true,
};

export const OPTIONS_DEFAULT = { skipIntroReturning: true };

export const CMS_DEFAULTS = { en, fr, images: IMAGES_DEFAULT, sections: SECTIONS_DEFAULT, options: OPTIONS_DEFAULT };

const LangContext = createContext(null);

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState(() => localStorage.getItem("settld-lang") || "en");
  const [remote, setRemote] = useState(null);
  useEffect(() => {
    localStorage.setItem("settld-lang", lang);
    document.documentElement.lang = lang === "fr" ? "fr-CA" : "en-CA";
  }, [lang]);
  useEffect(() => {
    fetch(`${API}/content`)
      .then((r) => r.json())
      .then((d) => {
        if (d?.content) setRemote(d.content);
      })
      .catch(() => {});
  }, []);
  const t = useMemo(() => mergeDeep(translations[lang], remote?.[lang]), [lang, remote]);
  const images = useMemo(() => mergeDeep(IMAGES_DEFAULT, remote?.images), [remote]);
  const sections = useMemo(() => mergeDeep(SECTIONS_DEFAULT, remote?.sections), [remote]);
  const options = useMemo(() => mergeDeep(OPTIONS_DEFAULT, remote?.options), [remote]);
  return (
    <LangContext.Provider value={{ lang, setLang, t, images, sections, options }}>
      {children}
    </LangContext.Provider>
  );
}

export const useLang = () => useContext(LangContext);
